import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React, {useEffect, useMemo, useRef} from 'react';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {Button} from 'src/design/atoms/Button';
import {FormBuilderContent} from 'src/app/form-builder/components/FormBuilderContent';
import {FormBuilderProvider} from 'src/app/form-builder/context/FormBuilderContext';
import {
  useGetFieldList,
  useUpdateFieldList,
} from 'src/app/form-builder/hooks/useFormBuilder';
import {
  useGetEquipmentTypeFieldsQuery,
  useSaveEquipmentTypeFieldsMutation,
} from 'src/redux/queries/equipmentType';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  mapFormFieldSimpleResponseToFormFieldV2,
  mapFormFieldsToFormFieldRequests,
} from 'src/types/FormV2Mapping';
import {fieldListHasInvalidMeasurementUnits} from './fieldEditorDefaults';
import {createInspectionsFieldRegistry} from './inspectionsFieldRegistry';

export interface FieldEditorProps {
  equipmentTypeId: number;
  equipmentTypeName?: string;
}

/**
 * InSpec's field editor: a thin composition over the shared
 * `app/form-builder` tree (field list/settings/preview) scoped to the
 * equipment-type field palette via `createInspectionsFieldRegistry`. This
 * component owns the equipment-type-specific bits: loading/saving fields
 * over RTK Query, and save-gating on measurement unit validity.
 */
export const FieldEditor: React.FC<FieldEditorProps> = ({
  equipmentTypeId,
  equipmentTypeName,
}) => (
  <FormBuilderProvider>
    <FieldEditorContent
      equipmentTypeId={equipmentTypeId}
      equipmentTypeName={equipmentTypeName}
    />
  </FormBuilderProvider>
);

const FieldEditorContent: React.FC<FieldEditorProps> = ({
  equipmentTypeId,
  equipmentTypeName,
}) => {
  const {setAlert} = useAlert();
  const registry = useMemo(
    () => createInspectionsFieldRegistry(equipmentTypeName),
    [equipmentTypeName]
  );
  const {data: fetchedFields, isLoading} = useGetEquipmentTypeFieldsQuery(
    equipmentTypeId,
    {skip: !equipmentTypeId}
  );
  const [saveFields, {isLoading: isSaving}] =
    useSaveEquipmentTypeFieldsMutation();

  const fieldList = useGetFieldList();
  const updateFieldList = useUpdateFieldList();

  // Stable reference so the seed effect below only depends on the fetched
  // data reference, not on the (identity-unstable) dispatch callback.
  const updateFieldListRef = useRef(updateFieldList);
  useEffect(() => {
    updateFieldListRef.current = updateFieldList;
  });

  // Seed (and re-seed, e.g. after a save invalidates/refetches the query)
  // the shared builder's field list from the server response.
  useEffect(() => {
    if (!fetchedFields) return;
    updateFieldListRef.current(
      fetchedFields.map(mapFormFieldSimpleResponseToFormFieldV2)
    );
  }, [fetchedFields]);

  const hasInvalidMeasurementUnits = useMemo(
    () => fieldListHasInvalidMeasurementUnits(fieldList),
    [fieldList]
  );

  const handleSave = async () => {
    try {
      await saveFields({
        id: equipmentTypeId,
        fields: mapFormFieldsToFormFieldRequests(fieldList),
      }).unwrap();
      setAlert('Inspection fields were successfully saved.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <InfoAlert>
        Editing fields here does not change past inspections.
      </InfoAlert>

      <Group justify="flex-end" gap="xs">
        {hasInvalidMeasurementUnits && (
          <Text size="xs" c="red">
            Measurement fields need at least one unit, with no blanks or
            duplicates, before saving.
          </Text>
        )}
        <Button
          variant="filled"
          color="yellow"
          onClick={handleSave}
          loading={isSaving}
          disabled={hasInvalidMeasurementUnits}>
          Save Fields
        </Button>
      </Group>

      <FormBuilderContent registry={registry} />
    </Stack>
  );
};
